import { Stat, Text } from "@chakra-ui/react"
import Figure, { PopOverProps } from "@hydrologiq/shared/figures/generic/Figure"
import { EnergyUnit } from "@hydrologiq/common/types/energy/consumption"
import { roundToDP } from "@hydrologiq/common/helpers/math"
import { formatYYYYMMDDHHMM } from "@hydrologiq/common/helpers/date"
import { DateTime } from "timezonecomplete"
import FigureContent from "@hydrologiq/shared/figures/generic/FigureContent"

export interface MinWeightProps {
  weight: number
  pressure?: number
}

export interface FuelLevelProps {
  currentLevel: number | null
  maxLevel: number | null
  offset?: number | null
  heading?: string
  refuellingTime?: DateTime | null
  unit: EnergyUnit
  minWeight?: MinWeightProps
  loading?: boolean
  error?: string
  isPlaceholder?: boolean
  compact?: boolean
}

function FuelLevel({
  currentLevel,
  offset,
  maxLevel,
  unit,
  minWeight,
  refuellingTime,
  heading = "Fuel level",
  loading,
  error,
  isPlaceholder,
  compact,
}: FuelLevelProps) {
  let actualCurrentLevel = typeof maxLevel === "number" && !Number.isNaN(currentLevel) ? currentLevel : undefined
  let actualRefuellingLevel = typeof maxLevel === "number" && !Number.isNaN(maxLevel) ? maxLevel : undefined
  if (actualCurrentLevel && actualRefuellingLevel && typeof offset === "number" && !Number.isNaN(offset)) {
    actualRefuellingLevel -= offset
    actualCurrentLevel -= offset
  }

  const level =
    typeof actualCurrentLevel === "number" && typeof actualRefuellingLevel === "number"
      ? actualCurrentLevel > 0 && actualRefuellingLevel > 0
        ? Math.min(100, Math.floor((actualCurrentLevel / actualRefuellingLevel) * 100))
        : 0
      : null

  const popover: undefined | PopOverProps =
    (minWeight && !Number.isNaN(minWeight.weight)) ||
      (refuellingTime instanceof DateTime && typeof maxLevel === "number")
      ? {
        title: "Fuel Level Information",
        content: (
          <>
            {refuellingTime instanceof DateTime && typeof maxLevel === "number" && (
              <>
                <Text>Last refuelling date: {formatYYYYMMDDHHMM(refuellingTime)}</Text>
                <Text>
                  Weight at last refuelling: {roundToDP(maxLevel, 1)}
                  {unit}
                </Text>
              </>
            )}

            {minWeight && !Number.isNaN(minWeight.weight) && typeof currentLevel === "number" && (
              <>
                <Text>
                  Current weight: {roundToDP(currentLevel, 1)}
                  {unit}
                </Text>
              </>
            )}
          </>
        ),
      }
      : undefined

  return (
    <Figure heading={heading} popover={popover} loading={loading} error={error} isPlaceholder={isPlaceholder} compact={compact}>
      <FigureContent.Root compact={compact}>
        <FigureContent.SubText compact={compact}>latest</FigureContent.SubText>
        <FigureContent.Value compact={compact}>{typeof level === "number" ? <>{level}%</> : "0%"}</FigureContent.Value>
        {!compact && typeof level === "number" && (
          <FigureContent.Label compact={compact}>of usable hydrogen</FigureContent.Label>
        )}
      </FigureContent.Root>
    </Figure>
  )
}

export default FuelLevel
